import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;
const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;
const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50%      { opacity: 0.3; }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;
const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;
const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 2em 2em;
  position: relative;
  min-height: 100vh;
  overflow-y: auto;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  /* ========== NEW MODULAR COMPONENT STYLES ========== */
  .tax-kpi-cards {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;

    @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 720px)  { grid-template-columns: repeat(2, 1fr); }

    .tax-kpi-card {
      ${({ theme }) => cardBase(theme)};
      padding: 14px 16px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-height: 80px;

      &:hover {
        border-color: ${({ theme }) => hairline(theme, true)};
        background: ${({ theme }) => surface(theme, 1)};
      }

      .card-content {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .thumb {
          opacity: 0.35;
          font-size: 0.85em;
          color: ${({ theme }) => theme.text};
        }

        .text p:first-child {
          ${mono};
          font-size: 1.25em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        .text p:last-child {
          font-size: 0.68em;
          font-weight: 500;
          opacity: 0.5;
          margin-top: 2px;
        }
      }
    }
  }

  /* ========== SHARED DONUT CHART STYLES ========== */
  .donut-wrapper {
    display: flex;
    flex-direction: row;
    gap: 24px;
    align-items: center;
    justify-content: center;
    width: 100%;

    .donut-chart-container,
    .donut-chart {
      position: relative;
      width: 200px;
      height: 200px;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-shrink: 0;
    }

    .donut-svg {
      width: 100%;
      height: 100%;
      overflow: visible;

      .donut-segment {
         transition: all 0.3s ease;
         cursor: pointer;
         
         path {
           transition: all 0.3s ease;
         }

         &:hover {
           transform: scale(1.05); 
           filter: drop-shadow(0 4px 6px rgba(0,0,0,0.2));
           z-index: 10;
           
           text {
             font-weight: 800;
             font-size: 12px;
           }
         }
      }
    }

    .donut-center {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      pointer-events: none;

      .donut-total {
        font-size: 1.6em;
        font-weight: 700;
        color: ${({ theme }) => theme.text};
        line-height: 1;
      }

      .donut-label {
        font-size: 0.7em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        margin-top: 4px;
        white-space: nowrap;
      }
    }

    .donut-content {
       display: flex;
       flex-direction: row;
       align-items: center;
       justify-content: center;
       gap: 24px;
       width: 100%;
    }

    .categories-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
      min-width: 200px;

      .category-item {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .category-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 3px;

          .category-color {
            width: 10px;
            height: 10px;
            border-radius: 2px;
          }

          .category-name {
            flex: 1;
            font-size: 0.8em;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
          }

          .category-count {
            font-size: 0.75em;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            background: ${({ theme }) => theme.buttonBg};
            padding: 2px 6px;
            border-radius: 4px;
          }
        }

        .category-bar {
          height: 4px;
          background: ${({ theme }) => theme.buttonBg};
          border-radius: 2px;
          overflow: hidden;

          .category-progress {
            height: 100%;
            border-radius: 2px;
            transition: width 0.3s ease;
          }
        }

        .category-value {
          font-size: 0.7em;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
          text-align: right;
          font-family: monospace;
        }
      }
    }
  }

  /* ========== COMPACT CATEGORIES CARD (Aba Geral) ========== */
  .compact-categories {
    max-width: 100%;
    background: ${({ theme }) => surface(theme, 0)};
    border-radius: ${tokens.radiusLg};
    padding: 10px;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 0;

    .donut-wrapper {
      flex-direction: column;
      gap: 14px;
      align-items: center;
      height: 100%;

      .donut-header {
        width: 100%;
        text-align: left;
        margin-bottom: 4px;

        h4 {
          margin: 0 0 3px 0;
          font-size: 0.82em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.01em;
        }

        p {
          margin: 0;
          font-size: 0.68em;
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          font-weight: 400;
        }
      }
      
      .donut-chart {
        width: 180px;
        height: 180px;
        flex-shrink: 0;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .donut-center {
        .donut-total {
          font-size: 1.5em;
          font-weight: 700;
          color: #fff;
          letter-spacing: -0.02em;
          line-height: 1;
        }
        .donut-label {
          font-size: 0.6em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          margin-top: 3px;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
      }

      .categories-list {
        width: 100%;
        flex: 1;
        min-height: 0;
        max-height: 340px;
        overflow-y: auto;
        padding-right: 6px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        
        &::-webkit-scrollbar { width: 3px; }
        &::-webkit-scrollbar-thumb {
          background: ${({ theme }) => hairline(theme, true)};
          border-radius: 2px;
        }

        .category-item {
          padding: 7px 10px;
          background: ${({ theme }) => surface(theme, 1)};
          border-radius: ${tokens.radiusSm};
          border: 1px solid ${({ theme }) => hairline(theme)};
          transition: border-color ${tokens.durBase} ${tokens.easeOut},
                      opacity ${tokens.durBase} ${tokens.easeOut};
          cursor: pointer;
          opacity: 0.7;

          &:hover {
            border-color: ${({ theme }) => hairline(theme, true)};
            opacity: 1;
          }

          .category-header {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 5px;

            .category-color {
              width: 6px;
              height: 6px;
              border-radius: 2px;
              flex-shrink: 0;
            }

            .category-name {
              flex: 1;
              font-size: 0.72em;
              font-weight: 500;
              color: ${({ theme }) => theme.text};
              letter-spacing: -0.005em;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .category-count {
              font-size: 0.65em;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              ${mono};
              opacity: 0.6;
            }
          }

          .category-bar {
            width: 100%;
            height: 3px;
            background: ${({ theme }) => hairline(theme)};
            border-radius: 2px;
            overflow: hidden;
            margin-bottom: 4px;
            box-sizing: border-box;

            .category-progress {
              height: 100%;
              border-radius: 2px;
              transition: width 0.3s ease;
            }
          }

          .category-value {
            font-size: 0.68em;
            ${mono};
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            text-align: right;
          }
        }
      }
    }
  }

  @keyframes tooltipFadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 2em 2em;

  /* ========== HEADER ========== */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 16px;
    flex-shrink: 0;

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "PRODUTOS · DASHBOARD";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandRing};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }

          &.error {
            color: ${tokens.danger};
            &::before { background: ${tokens.danger}; box-shadow: 0 0 8px rgba(239,68,68,0.28); }
          }
        }

        .update-tag {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          ${mono};
          font-size: 0.9em;
          &::before { content: "· "; opacity: 0.5; }
        }

        .refresh-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 5px;
          background: transparent;
          border: none;
          border-radius: ${tokens.radiusSm};
          cursor: pointer;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          transition: all ${tokens.durBase} ${tokens.easeOut};

          &:hover { opacity: 1; background: ${({ theme }) => surface(theme, 1)}; }
          &:disabled { opacity: 0.25; cursor: not-allowed; }
          svg { font-size: 0.85em; }
        }
      }
    }
  }

  .spin { animation: ${spin} 1s linear infinite; }
  .skeleton-loading { animation: ${skeletonPulse} 1.5s ease-in-out infinite; opacity: 0.6; }

  /* ========== LOADING, ERROR, EMPTY STATES ========== */
  .loading-state,
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 110px 20px 80px;
    text-align: center;

    h4 {
      margin: 0;
      font-size: 1em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.74em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      letter-spacing: 0.01em;
      max-width: 280px;
      line-height: 1.55;
    }
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 110px 20px 80px;
    background: transparent;
    border: none;
    text-align: center;

    .empty-icon {
      width: 64px;
      height: 64px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: 50%;
      color: ${tokens.brand};
      opacity: 0.7;

      svg { font-size: 1.6em; }
    }

    h4 {
      margin: 0;
      font-size: 1em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.74em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      letter-spacing: 0.01em;
      max-width: 280px;
      line-height: 1.55;
    }

    .empty-cta {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 9px 18px;
      background: ${tokens.brand};
      color: white;
      border: 1px solid ${tokens.brand};
      border-radius: ${tokens.radiusMd};
      font-size: 0.8em;
      font-weight: 500;
      cursor: pointer;
      margin-top: 8px;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      &:hover {
        background: ${tokens.brandHover};
        transform: translateY(-1px);
      }
    }
  }

  /* ========== TABS HEADER ========== */
  .tabs-header {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
    flex-shrink: 0;

    .tab {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: transparent;
      border: 1px solid transparent;
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      ${mono};
      text-transform: uppercase;
      letter-spacing: 0.04em;

      &:hover { opacity: 0.85; background: ${({ theme }) => surface(theme, 1)}; }

      &.active {
        opacity: 1;
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
      }

      svg { font-size: 0.95em; }
    }
  }

  /* ========== STATS TAB ========== */
  .stats-tab {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;

    .stats-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 40px;
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;

      .stats-icon {
        font-size: 48px;
        color: ${({ theme }) => theme.primary};
        opacity: 0.5;
        margin-bottom: 16px;
      }

      h4 {
        margin: 0 0 8px 0;
        font-size: 1.2em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        opacity: 0.9;
      }

      p {
        margin: 0;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
      }
    }
  }

  /* ========== KPI CARDS ========== */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;

    &.main-stats { margin-bottom: 12px; }
    &.tax-stats { grid-template-columns: repeat(6, 1fr); }
    &.secondary-stats { grid-template-columns: repeat(5, 1fr); }

    @media (max-width: 1200px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 500px)  { grid-template-columns: 1fr; }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .thumb {
        opacity: 0.35;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        display: inline-flex;
        align-items: center;
      }

      .text {
        display: flex;
        flex-direction: column;
        gap: 2px;

        p { margin: 0; }

        p:first-child {
          ${mono};
          font-size: 1.6em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        p:last-child {
          font-size: 0.72em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          margin-top: 2px;
          letter-spacing: -0.005em;
        }
      }
    }

    &.success .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
    &.warning .card-content .thumb { color: ${tokens.info};  opacity: 0.65; }
    &.danger  .card-content .thumb { color: ${tokens.danger}; opacity: 0.65; }
    &.info    .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }
    &.primary .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
    &.cte     .card-content .thumb { color: ${tokens.info};  opacity: 0.65; }
    &.nfe     .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }

    &.compact {
      min-height: 72px;
      padding: 12px 14px;
      .text p:first-child { font-size: 1.35em; }
      .text p:last-child { font-size: 0.68em; }
    }
  }

  /* ========== FILTERS / TOOLBAR ========== */
  .filters {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;
    flex-wrap: wrap;

    .search-input-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 12px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      flex: 1;
      max-width: 360px;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &:focus-within { border-color: ${tokens.brandRing}; }

      .search-icon {
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 0.9em;
        flex-shrink: 0;
      }

      .search-input {
        background: transparent;
        border: none;
        outline: none;
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        flex: 1;
        letter-spacing: -0.005em;

        &::placeholder { opacity: 0.4; }
      }
    }

    @media (max-width: 900px) {
      .search-input-wrapper { max-width: none; }
    }
  }

  /* ========== CATEGORIES CARD ========== */
  .categories-card {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    ${({ theme }) => cardBase(theme)};
    overflow: hidden;

    .card-header {
      padding: 14px 16px;
      display: flex;
      align-items: center;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      svg {
        margin-right: 8px;
        opacity: 0.4;
        color: ${({ theme }) => theme.text};
        font-size: 0.9em;
      }

      h4 {
        margin: 0;
        font-size: 0.82em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.005em;
      }
    }

    .chart-section {
      padding: 16px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .categories-list {
      flex: 1;
      overflow-y: auto;
      padding: 0 4px 8px 4px;

      &::-webkit-scrollbar { width: 4px; }
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => hairline(theme, true)};
        border-radius: 2px;
      }

      .category-item {
        padding: 10px 14px;
        border-bottom: 1px solid ${({ theme }) => hairline(theme)};

        &:last-child { border-bottom: none; }

        .category-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 5px;

          .category-color {
            width: 8px;
            height: 8px;
            border-radius: 2px;
          }

          .category-name {
            flex: 1;
            font-size: 0.72em;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
            letter-spacing: -0.005em;
          }

          .category-count {
            font-size: 0.68em;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            ${mono};
            opacity: 0.6;
          }
        }

        .category-details {
          display: flex;
          justify-content: space-between;
          margin-bottom: 5px;

          .category-percentage,
          .category-revenue {
            font-size: 0.65em;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }

          .category-revenue {
            ${mono};
            font-weight: 600;
          }
        }

        .category-bar {
          width: 100%;
          height: 3px;
          background: transparent;
          border-radius: 2px;
          overflow: hidden;

          .category-progress {
            height: 100%;
            border-radius: 2px;
            transition: width 0.3s ease;
          }
        }
      }
    }
  }

  /* ========== MONTHLY CHART ========== */
  .monthly-chart {
    width: 100%;
    ${({ theme }) => cardBase(theme)};
    overflow: hidden;
    margin-top: 24px;

    .chart-header {
      padding: 16px 20px;
      display: flex;
      align-items: center;

      svg {
        margin-right: 8px;
        opacity: 0.7;
        color: ${({ theme }) => theme.text};
        font-size: 16px;
      }

      h4 {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }
    }

    .chart-content {
      display: grid;
      grid-template-columns: 280px 1fr;
      gap: 0;
      min-height: 320px;

      @media (max-width: 900px) {
        grid-template-columns: 1fr;
      }
    }

    .month-info-card {
      border-right: 1px solid ${({ theme }) => theme.border};
      padding: 20px;

      .month-info-header {
        margin-bottom: 20px;

        h5 {
          margin: 0 0 4px 0;
          font-size: 18px;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
        }

        .month-index {
          font-size: 12px;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }

      .month-metrics {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .metric-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          background: ${({ theme }) => theme.card};
          border: 1px solid ${({ theme }) => theme.border};
          border-radius: 6px;

          &.compras {
            border-left: 4px solid ${tokens.info};
          }
          &.vendas {
            border-left: 4px solid ${tokens.brand};
          }
          &.impostos {
            border-left: 4px solid ${tokens.danger};
          }

          .metric-icon {
            font-size: 16px;
            margin-top: 2px;
            opacity: 0.7;
          }

          .metric-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 2px;

            .metric-label {
              font-size: 11px;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }

            .metric-value {
              font-size: 16px;
              font-weight: 700;
              color: ${({ theme }) => theme.text};
              font-family: monospace;
            }

            .impostos-breakdown {
              display: flex;
              flex-direction: column;
              gap: 4px;
              margin-top: 8px;

              .imposto-item {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 10px;
                color: ${({ theme }) => theme.text};
                opacity: 0.6;

                .imposto-color {
                  width: 8px;
                  height: 8px;
                  border-radius: 50%;
                }
              }
            }
          }
        }
      }
    }

    .chart-container {
      padding: 20px;

      * {
        outline: none !important;
      }

      *:focus {
        outline: none !important;
      }

      .chart-header-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        h4 {
          margin: 0;
          font-size: 0.95em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }

        .chart-legend {
          display: flex;
          gap: 16px;
          margin-bottom: 0;

          .legend-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;

            .legend-color {
              width: 10px;
              height: 10px;
              border-radius: 2px;
            }
          }
        }
      }

      .chart-legend {
        display: flex;
        gap: 16px;
        margin-bottom: 16px;
        justify-content: center;

        .legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;

          .legend-color {
            width: 10px;
            height: 10px;
            border-radius: 2px;
          }
        }
      }

      .chart-placeholder {
        height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${({ theme }) => theme.contrast};
        border-radius: 8px;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 14px;
      }

      /* Transição suave nas barras do gráfico */
      .recharts-bar-rectangle {
        transition: opacity 0.2s ease;
      }

      /* Remover borda do gráfico */
      .recharts-wrapper,
      .recharts-wrapper *,
      .recharts-surface,
      .recharts-responsive-container {
        outline: none !important;
        border: none !important;
      }

      .recharts-wrapper:focus,
      .recharts-surface:focus {
        outline: none !important;
      }

      .recharts-cartesian-grid-bg {
        fill: transparent !important;
        stroke: transparent !important;
      }

      .recharts-cartesian-grid rect {
        stroke: transparent !important;
      }

      /* Tooltip Customizado */
      .recharts-tooltip-wrapper {
        outline: none !important;
        border: none !important;
      }

      .custom-tooltip {
        background: ${({ theme }) => theme.card};
        border: none;
        outline: none;
        border-radius: 12px;
        padding: 16px;
        min-width: 200px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);

        .tooltip-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid ${({ theme }) => theme.border};

          .tooltip-month {
            font-size: 1em;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
          }

          .tooltip-index {
            font-size: 0.7em;
            font-weight: 600;
            color: ${({ theme }) => theme.primary};
            background: rgba(16, 185, 129, 0.1);
            padding: 3px 8px;
            border-radius: 4px;
          }
        }

        .tooltip-content {
          display: flex;
          flex-direction: column;
          gap: 10px;

          .tooltip-item {
            display: flex;
            align-items: center;
            gap: 8px;

            .tooltip-dot {
              width: 10px;
              height: 10px;
              border-radius: 3px;
              flex-shrink: 0;
            }

            .tooltip-label {
              flex: 1;
              font-size: 0.8em;
              color: ${({ theme }) => theme.text};
              opacity: 0.7;
            }

            .tooltip-value {
              font-size: 0.85em;
              font-weight: 700;
              color: ${({ theme }) => theme.text};
              font-family: "Poppins", monospace;
            }
          }
        }

        .tooltip-footer {
          margin-top: 12px;
          padding-top: 10px;
          border-top: 1px dashed ${({ theme }) => theme.border};
          text-align: center;

          span {
            font-size: 0.7em;
            color: ${({ theme }) => theme.primary};
            font-weight: 500;
            opacity: 0.8;
          }
        }
      }
    }
  }

  /* ========== STATS TAB ========== */
  .stats-tab {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px 0;
    width: 100%;
    align-self: stretch;

    /* Container para as duas linhas de gráficos */
    .charts-container {
      display: flex;
      flex-direction: column;
      gap: 24px;
      width: 100%;
    }

    /* Charts Row - Donut e Barras lado a lado */
    .charts-row {
      display: flex;
      justify-content: space-between;
      gap: 24px;
      width: 100%;

      .stats-section.chart-card:first-child {
        flex: 0 0 40%;
      }

      .stats-section.chart-card:last-child {
        flex: 1;
      }
    }

    .stats-section {
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      padding: 24px;

      &.chart-card {
        min-height: 400px;
        display: flex;
        flex-direction: column;
      }

      &.chart-card.compact-categories {
        background: ${({ theme }) => surface(theme, 0)};
        border: none;
        border-radius: ${tokens.radiusLg};
        padding: 16px;
        height: 100%;
        min-height: 0;
      }

      .section-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 20px;
        padding-bottom: 12px;
        border-bottom: 1px solid ${({ theme }) => theme.border};

        svg {
          font-size: 1.2em;
          color: ${({ theme }) => theme.primary};
          opacity: 0.8;
        }

        h4 {
          margin: 0;
          font-size: 0.95em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }
      }



      /* Bar Chart Wrapper */
      .bar-chart-wrapper {
        flex: 1;
        display: flex;
        flex-direction: column;
        width: 100%;
        .chart-legend {
          display: flex;
          gap: 16px;
          margin-bottom: 16px;

          .legend-item {
            display: flex;
            align-items: center;
            gap: 6px;

            .legend-color {
              width: 10px;
              height: 10px;
              border-radius: 2px;
            }

            span {
              font-size: 0.75em;
              color: ${({ theme }) => theme.text};
              opacity: 0.7;
            }
          }
        }

        svg {
          width: 100%;
          flex: 1;
          height: auto;
        }
      }

      /* Tooltip Customizado do Gráfico */
      .chart-tooltip {
        background: rgba(17, 24, 39, 0.95);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        padding: 18px 24px;
        min-width: 260px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
        backdrop-filter: blur(10px);

        .tooltip-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);

          .tooltip-month {
            font-size: 0.85em;
            font-weight: 600;
            color: #fff;
          }

          .tooltip-type {
            font-size: 0.75em;
            color: #9ca3af;
            background: rgba(255, 255, 255, 0.05);
            padding: 3px 8px;
            border-radius: 4px;
          }
        }

        .tooltip-content {
          .tooltip-value {
            font-size: 1.4em;
            font-weight: 700;
            margin-bottom: 4px;

            &.compras {
              color: ${tokens.info};
            }

            &.vendas {
              color: ${tokens.brand};
            }
          }

          .tooltip-label {
            font-size: 0.75em;
            color: #9ca3af;
          }

          .tooltip-taxes {
            display: flex;
            flex-direction: column;
            gap: 10px;

            .tax-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 40px;

              .tax-name {
                font-size: 0.85em;
                color: #9ca3af;
              }

              .tax-value {
                font-size: 0.9em;
                font-weight: 600;
                color: #fff;
                text-align: right;
              }
            }
          }

          .tooltip-total {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 12px;
            padding-top: 10px;
            border-top: 1px solid rgba(255, 255, 255, 0.1);

            span {
              font-size: 0.85em;
              color: #9ca3af;
            }

            .total-value {
              font-size: 1.1em;
              font-weight: 700;
              color: ${tokens.danger};
            }
          }
        }
      }
    }

    /* Rankings Row - 3 cards com mesmo tamanho */
    .rankings-row {
      display: flex;
      justify-content: space-between;
      gap: 24px;
      width: 100%;
    }

    .ranking-card {
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      padding: 20px;
      display: flex;
      flex-direction: column;
      flex: 1;
      height: 380px;

      .ranking-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid ${({ theme }) => theme.border};

        svg {
          font-size: 1.1em;
          color: ${({ theme }) => theme.primary};
          opacity: 0.8;
        }

        h4 {
          margin: 0;
          font-size: 0.9em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }
      }

      .ranking-list {
        flex: 1;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 8px;

        &::-webkit-scrollbar {
          width: 4px;
        }

        &::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 2px;
        }

        &::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 2px;
        }

        .ranking-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 8px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          border-bottom: 1px solid ${({ theme }) => theme.border};

          &:hover {
            background: rgba(255, 255, 255, 0.03);
          }

          &:last-child {
            border-bottom: none;
          }

          .ranking-thumb {
            width: 36px;
            height: 36px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            flex-shrink: 0;
            border: 1px solid rgba(255, 255, 255, 0.1);

            &.faturamento {
              background: rgba(16, 185, 129, 0.15);
              color: ${tokens.brand};
            }

            &.margem {
              background: rgba(59, 130, 246, 0.15);
              color: ${tokens.info};
            }

            &.devolucao {
              background: rgba(239, 68, 68, 0.15);
              color: ${tokens.danger};
            }
          }

          .ranking-info {
            flex: 1;
            min-width: 0;

            .ranking-name {
              font-size: 0.8em;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              display: block;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              margin-bottom: 4px;
            }

            .ranking-tags {
              display: flex;
              gap: 4px;
              flex-wrap: wrap;

              .category-tag,
              .code-tag {
                font-size: 0.65em;
                padding: 2px 5px;
                border-radius: 3px;
                font-weight: 500;
              }

              .category-tag {
                background: rgba(59, 130, 246, 0.15);
                color: ${tokens.info};
                border: 1px solid rgba(59, 130, 246, 0.25);
              }

              .code-tag {
                background: rgba(156, 163, 175, 0.15);
                color: #9ca3af;
                border: 1px solid rgba(156, 163, 175, 0.25);
              }
            }
          }

          .ranking-value {
            font-size: 0.85em;
            font-weight: 700;
            font-family: monospace;
            flex-shrink: 0;

            &.faturamento {
              color: ${tokens.brand};
            }

            &.margem {
              color: ${tokens.info};
            }

            &.devolucao {
              color: ${tokens.danger};
            }
          }
        }
      }
    }

    /* Summary Cards Row */
    .summary-cards-row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;

      .summary-card {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 20px;
        background: ${({ theme }) => theme.card};
        border-radius: 12px;
        border: 1px solid ${({ theme }) => theme.border};

        .summary-icon {
          font-size: 1.4em;
          padding: 12px;
          border-radius: 10px;

          &.compras {
            color: ${tokens.info};
            background: rgba(59, 130, 246, 0.1);
          }

          &.vendas {
            color: ${tokens.brand};
            background: rgba(16, 185, 129, 0.1);
          }

          &.impostos {
            color: ${tokens.danger};
            background: rgba(239, 68, 68, 0.1);
          }
        }

        .summary-content {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .summary-label {
            font-size: 0.8em;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
          }

          .summary-value {
            font-size: 1.2em;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
          }
        }
      }
    }

    /* KPIs Executivos */
    .executive-kpis-row {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 16px;
      margin-top: 24px;

      .exec-kpi-card {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 20px;
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 12px;
        transition: all 0.2s ease;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        }

        .kpi-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          font-size: 1.3em;

          &.blue {
            background: rgba(59, 130, 246, 0.1);
            color: ${tokens.info};
          }
          &.green {
            background: rgba(16, 185, 129, 0.1);
            color: ${tokens.brand};
          }
          &.orange {
            background: rgba(245, 158, 11, 0.1);
            color: ${tokens.warn};
          }
          &.red {
            background: rgba(239, 68, 68, 0.1);
            color: ${tokens.danger};
          }
          &.purple {
            background: rgba(139, 92, 246, 0.1);
            color: ${tokens.warn};
          }
        }

        .kpi-content {
          display: flex;
          flex-direction: column;
          gap: 2px;

          .kpi-value {
            font-size: 1.4em;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
            font-family: "Poppins", sans-serif;
          }

          .kpi-label {
            font-size: 0.8em;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
          }

          .kpi-change {
            font-size: 0.75em;
            font-weight: 600;

            &.positive {
              color: ${tokens.brand};
            }
            &.negative {
              color: ${tokens.danger};
            }
            &.neutral {
              color: ${tokens.warn};
            }
          }
        }
      }
    }

    /* Gráficos Avançados */
    .advanced-charts-row {
      display: grid;
      grid-template-columns: 1.5fr 1fr;
      gap: 20px;
      margin-top: 24px;

      .advanced-chart-card {
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 16px;
        padding: 20px;

        .chart-header-advanced {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;

          .chart-title-group {
            display: flex;
            align-items: center;
            gap: 10px;

            .chart-icon {
              font-size: 1.2em;
              color: ${({ theme }) => theme.primary};
            }

            h4 {
              font-size: 1em;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              margin: 0;
            }
          }

          .chart-subtitle {
            font-size: 0.8em;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }

          .chart-period-selector {
            display: flex;
            gap: 4px;

            .period-btn {
              padding: 6px 12px;
              border: 1px solid ${({ theme }) => theme.border};
              border-radius: 6px;
              background: transparent;
              color: ${({ theme }) => theme.text};
              font-size: 0.75em;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s ease;

              &:hover {
                background: ${({ theme }) => theme.buttonBg};
              }

              &.active {
                background: ${({ theme }) => theme.primary};
                border-color: ${({ theme }) => theme.primary};
                color: #fff;
              }
            }
          }
        }

        .chart-body {
          &.tax-composition {
            display: flex;
            align-items: center;
            gap: 24px;
            padding: 10px 0;

            .tax-donut-container {
              flex-shrink: 0;
            }

            .tax-legend-list {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 12px;

              .tax-legend-item {
                display: flex;
                align-items: center;
                gap: 12px;

                .tax-color {
                  width: 12px;
                  height: 12px;
                  border-radius: 3px;

                  &.icms {
                    background: linear-gradient(135deg, ${tokens.info}, #1d4ed8);
                  }
                  &.pis {
                    background: linear-gradient(135deg, ${tokens.brand}, #059669);
                  }
                  &.cofins {
                    background: linear-gradient(135deg, ${tokens.warn}, #d97706);
                  }
                  &.ipi {
                    background: linear-gradient(135deg, ${tokens.warn}, #7c3aed);
                  }
                }

                .tax-info {
                  flex: 1;
                  display: flex;
                  align-items: center;
                  gap: 8px;

                  .tax-name {
                    font-size: 0.9em;
                    font-weight: 500;
                    color: ${({ theme }) => theme.text};
                  }

                  .tax-percent {
                    font-size: 0.75em;
                    color: ${({ theme }) => theme.text};
                    opacity: 0.5;
                  }
                }

                .tax-value {
                  font-size: 0.9em;
                  font-weight: 600;
                  color: ${({ theme }) => theme.text};
                  font-family: "Poppins", sans-serif;
                }
              }
            }
          }
        }
      }
    }

    /* Tabela de Performance por Categoria */
    .category-performance-section {
      margin-top: 24px;
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 16px;
      overflow: hidden;

      .section-header-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px 24px;
        border-bottom: 1px solid ${({ theme }) => theme.border};

        .section-title {
          display: flex;
          align-items: center;
          gap: 10px;

          .section-icon {
            font-size: 1.3em;
            color: ${({ theme }) => theme.primary};
          }

          h3 {
            font-size: 1.1em;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            margin: 0;
          }
        }

        .section-subtitle {
          font-size: 0.85em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }
      }

      .performance-table-container {
        overflow-x: auto;
      }

      .performance-table {
        width: 100%;
        border-collapse: collapse;

        thead tr th {
          padding: 14px 16px;
          font-size: 0.75em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          text-align: left;
          background: ${({ theme }) => theme.cardBg};
          border-bottom: 1px solid ${({ theme }) => theme.border};
        }

        tbody tr.data-row {
          transition: background 0.2s ease;

          &:hover {
            background: ${({ theme }) => surface(theme, 1)};
          }

          td {
            padding: 16px;
            font-size: 0.9em;
            color: ${({ theme }) => theme.text};
            border-bottom: 1px solid ${({ theme }) => theme.border};

            .categoria-badge {
              display: flex;
              align-items: center;
              gap: 10px;

              .cat-dot {
                width: 10px;
                height: 10px;
                border-radius: 3px;
                background: var(--cat-color);
              }

              .cat-name {
                font-weight: 600;
              }
            }

            .produtos-count {
              font-weight: 600;
              color: ${({ theme }) => theme.text};
            }

            .valor-destaque {
              font-weight: 700;
              font-family: "Poppins", sans-serif;
              color: ${({ theme }) => theme.text};
            }

            .participacao-bar-container {
              display: flex;
              align-items: center;
              gap: 10px;
              min-width: 120px;

              .participacao-bar {
                height: 8px;
                border-radius: 4px;
                min-width: 4px;
              }

              .participacao-value {
                font-size: 0.85em;
                font-weight: 500;
                color: ${({ theme }) => theme.text};
                opacity: 0.7;
              }
            }

            .margem-value {
              font-weight: 700;
              padding: 4px 10px;
              border-radius: 6px;

              &.high {
                background: rgba(16, 185, 129, 0.15);
                color: ${tokens.brand};
              }
              &.medium {
                background: rgba(59, 130, 246, 0.15);
                color: ${tokens.info};
              }
              &.low {
                background: rgba(245, 158, 11, 0.15);
                color: ${tokens.warn};
              }
            }

            .impostos-value {
              font-weight: 600;
              color: ${tokens.danger};
            }

            .tendencia-badge {
              display: inline-block;
              padding: 5px 12px;
              border-radius: 20px;
              font-size: 0.8em;
              font-weight: 600;

              &.up {
                background: rgba(16, 185, 129, 0.15);
                color: ${tokens.brand};
              }
              &.down {
                background: rgba(239, 68, 68, 0.15);
                color: ${tokens.danger};
              }
              &.stable {
                background: rgba(156, 163, 175, 0.15);
                color: #9ca3af;
              }
            }
          }
        }
      }
    }

    /* Insights e Alertas */
    .insights-row {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 20px;
      margin-top: 24px;

      .insights-card,
      .alerts-card,
      .quick-stats-card {
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 16px;
        padding: 20px;
      }

      .insights-header,
      .alerts-header,
      .quick-stats-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 16px;

        svg {
          font-size: 1.2em;
          color: ${({ theme }) => theme.primary};
        }

        h4 {
          font-size: 1em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          margin: 0;
        }
      }

      .insights-list {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .insight-item {
          display: flex;
          gap: 12px;
          padding: 12px;
          border-radius: 10px;
          background: ${({ theme }) => theme.cardBg};

          .insight-indicator {
            width: 4px;
            border-radius: 2px;
            flex-shrink: 0;
          }

          &.positive .insight-indicator {
            background: #10b981;
          }
          &.warning .insight-indicator {
            background: #f59e0b;
          }
          &.info .insight-indicator {
            background: #3b82f6;
          }

          .insight-content {
            .insight-title {
              font-size: 0.9em;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              display: block;
              margin-bottom: 4px;
            }

            p {
              font-size: 0.8em;
              color: ${({ theme }) => theme.text};
              opacity: 0.7;
              margin: 0;
              line-height: 1.4;
            }
          }
        }
      }

      .alerts-header {
        .alerts-count {
          margin-left: auto;
          font-size: 0.7em;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 12px;
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }
      }

      .alerts-list {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .alert-item {
          display: flex;
          gap: 12px;
          padding: 14px;
          border-radius: 10px;
          background: ${({ theme }) => theme.cardBg};
          border: 1px solid ${({ theme }) => theme.border};
          transition: all 0.2s ease;

          &:hover {
            transform: translateX(4px);
          }

          &.critical:hover {
            border-color: #ef4444;
          }
          &.warning:hover {
            border-color: #f59e0b;
          }
          &.info:hover {
            border-color: #3b82f6;
          }

          .alert-icon-wrapper {
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 8px;
            font-size: 1em;
            flex-shrink: 0;

            &.critical {
              background: rgba(239, 68, 68, 0.1);
              color: ${tokens.danger};
            }
            &.warning {
              background: rgba(245, 158, 11, 0.1);
              color: ${tokens.warn};
            }
            &.info {
              background: rgba(59, 130, 246, 0.1);
              color: ${tokens.info};
            }
          }

          .alert-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 6px;

            .alert-header-row {
              display: flex;
              align-items: center;
              justify-content: space-between;

              .alert-badge {
                font-size: 0.65em;
                font-weight: 700;
                padding: 3px 8px;
                border-radius: 4px;
                text-transform: uppercase;
              }

              .alert-count {
                font-size: 1.1em;
                font-weight: 700;
                font-family: "Poppins", sans-serif;
              }
            }

            .alert-text {
              font-size: 0.8em;
              color: ${({ theme }) => theme.text};
              opacity: 0.7;
            }

            .alert-progress {
              height: 4px;
              background: ${({ theme }) => theme.border};
              border-radius: 2px;
              overflow: hidden;

              .progress-bar {
                height: 100%;
                border-radius: 2px;
                transition: width 0.3s ease;

                &.critical {
                  background: linear-gradient(90deg, #ef4444, #dc2626);
                }
                &.warning {
                  background: linear-gradient(90deg, #f59e0b, #d97706);
                }
                &.info {
                  background: linear-gradient(90deg, #3b82f6, #2563eb);
                }
              }
            }
          }

          &.critical {
            .alert-header-row .alert-badge {
              background: rgba(239, 68, 68, 0.15);
              color: ${tokens.danger};
            }
            .alert-header-row .alert-count {
              color: ${tokens.danger};
            }
          }
          &.warning {
            .alert-header-row .alert-badge {
              background: rgba(245, 158, 11, 0.15);
              color: ${tokens.warn};
            }
            .alert-header-row .alert-count {
              color: ${tokens.warn};
            }
          }
          &.info {
            .alert-header-row .alert-badge {
              background: rgba(59, 130, 246, 0.15);
              color: ${tokens.info};
            }
            .alert-header-row .alert-count {
              color: ${tokens.info};
            }
          }
        }
      }

      .alerts-footer {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid ${({ theme }) => theme.border};

        .view-all-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px;
          border: 1px solid ${({ theme }) => theme.border};
          border-radius: 8px;
          background: transparent;
          color: ${({ theme }) => theme.text};
          font-size: 0.8em;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: ${({ theme }) => theme.primary};
            border-color: ${({ theme }) => theme.primary};
            color: #fff;
          }

          svg {
            font-size: 1em;
          }
        }
      }

      .quick-stats-header {
        .header-badge {
          margin-left: auto;
          font-size: 0.65em;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 12px;
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }

      .quick-stats-grid {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .quick-stat {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          background: ${({ theme }) => theme.cardBg};
          border-radius: 10px;
          border: 1px solid ${({ theme }) => theme.border};
          transition: all 0.2s ease;

          &:hover {
            transform: translateX(4px);
            border-color: ${({ theme }) => theme.primary};
          }

          .stat-icon-wrapper {
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 8px;
            font-size: 1em;
            flex-shrink: 0;

            &.blue {
              background: rgba(59, 130, 246, 0.1);
              color: ${tokens.info};
            }
            &.green {
              background: rgba(16, 185, 129, 0.1);
              color: ${tokens.brand};
            }
            &.orange {
              background: rgba(245, 158, 11, 0.1);
              color: ${tokens.warn};
            }
            &.purple {
              background: rgba(139, 92, 246, 0.1);
              color: ${tokens.warn};
            }
          }

          .stat-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 2px;

            .stat-number {
              font-size: 1.2em;
              font-weight: 700;
              color: ${({ theme }) => theme.text};
              font-family: "Poppins", sans-serif;
            }

            .stat-label {
              font-size: 0.7em;
              color: ${({ theme }) => theme.text};
              opacity: 0.5;
            }
          }

          .stat-trend {
            font-size: 0.75em;
            font-weight: 700;
            padding: 4px 8px;
            border-radius: 6px;

            &.up {
              background: rgba(16, 185, 129, 0.15);
              color: ${tokens.brand};
            }
            &.down {
              background: rgba(239, 68, 68, 0.15);
              color: ${tokens.danger};
            }
            &.neutral {
              background: rgba(156, 163, 175, 0.15);
              color: #9ca3af;
            }
          }
        }
      }

      .quick-stats-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid ${({ theme }) => theme.border};

        .footer-text {
          font-size: 0.7em;
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
        }

        .refresh-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border: none;
          border-radius: 8px;
          background: ${({ theme }) => theme.primary};
          color: #fff;
          font-size: 0.75em;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          }

          svg {
            font-size: 1em;
          }
        }
      }
    }
  }

  @media (max-width: 1400px) {
    .stats-tab {
      .executive-kpis-row {
        grid-template-columns: repeat(3, 1fr);
      }
    }
  }

  @media (max-width: 1200px) {
    .stats-tab {
      .rankings-row {
        grid-template-columns: 1fr;
      }
    }
  }

  @media (max-width: 1024px) {
    .stats-tab {
      .charts-row {
        grid-template-columns: 1fr;
      }

      .summary-cards-row {
        grid-template-columns: 1fr;
      }
    }
  }

  /* ========== REFORMA TRIBUTÁRIA TAB ========== */
  .reforma-tab {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px 0;
    width: 100%;
    align-self: stretch;

    .reforma-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px;
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;

      .reforma-title {
        display: flex;
        align-items: center;
        gap: 16px;

        svg {
          font-size: 2.5em;
          color: #f59e0b;
        }

        h2 {
          margin: 0;
          font-size: 1.4em;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
        }

        p {
          margin: 4px 0 0 0;
          font-size: 0.85em;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
        }
      }

      .reforma-badge {
        display: flex;
        gap: 8px;

        .badge-new {
          padding: 6px 12px;
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
          border-radius: 6px;
          font-size: 0.75em;
          font-weight: 600;
        }

        .badge-year {
          padding: 6px 12px;
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
          border-radius: 6px;
          font-size: 0.75em;
          font-weight: 600;
        }
      }
    }

    .reforma-comparison {
      display: flex;
      gap: 24px;
      align-items: stretch;

      .sistema-card {
        flex: 1;
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 12px;
        padding: 24px;

        &.atual {
          border-left: 4px solid ${tokens.danger};
        }

        &.novo {
          border-left: 4px solid ${tokens.brand};
        }

        .sistema-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 1px solid ${({ theme }) => theme.border};

          h3 {
            margin: 0;
            font-size: 1.1em;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
          }

          .sistema-tag {
            padding: 4px 10px;
            background: rgba(239, 68, 68, 0.15);
            color: #ef4444;
            border-radius: 4px;
            font-size: 0.7em;
            font-weight: 600;

            &.novo {
              background: rgba(16, 185, 129, 0.15);
              color: ${tokens.brand};
            }
          }
        }

        .sistema-impostos {
          display: flex;
          flex-direction: column;
          gap: 16px;

          .imposto-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px;
            background: ${({ theme }) => theme.buttonBg};
            border-radius: 8px;

            .imposto-info {
              display: flex;
              flex-direction: column;
              gap: 2px;

              .imposto-nome {
                font-size: 0.9em;
                font-weight: 600;
                color: ${({ theme }) => theme.text};
              }

              .imposto-desc {
                font-size: 0.7em;
                color: ${({ theme }) => theme.text};
                opacity: 0.5;
              }
            }

            .imposto-valor {
              font-size: 1em;
              font-weight: 700;
              color: ${({ theme }) => theme.text};
              font-family: monospace;
            }
          }
        }

        .sistema-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid ${({ theme }) => theme.border};

          span {
            font-size: 0.9em;
            color: ${({ theme }) => theme.text};
            opacity: 0.7;
          }

          .total-valor {
            font-size: 1.3em;
            font-weight: 700;
            color: #ef4444;
            opacity: 1;

            &.novo {
              color: ${tokens.brand};
            }
          }
        }
      }

      .transicao-arrow {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 0 16px;

        svg {
          font-size: 2em;
          color: #f59e0b;
        }

        span {
          font-size: 0.7em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
      }
    }

    .reforma-timeline {
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      padding: 24px;

      h3 {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0 0 20px 0;
        font-size: 1em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};

        svg {
          color: #3b82f6;
        }
      }

      .timeline-items {
        display: flex;
        gap: 16px;

        .timeline-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 16px;
          background: ${({ theme }) => theme.buttonBg};
          border-radius: 8px;
          border-left: 3px solid ${({ theme }) => theme.border};
          transition: all 0.2s ease;

          &.active {
            border-left-color: #10b981;
            background: rgba(16, 185, 129, 0.05);
          }

          .timeline-year {
            font-size: 0.85em;
            font-weight: 700;
            color: #3b82f6;
          }

          .timeline-content {
            h4 {
              margin: 0 0 4px 0;
              font-size: 0.85em;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
            }

            p {
              margin: 0;
              font-size: 0.75em;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
              line-height: 1.4;
            }
          }
        }
      }
    }

    .reforma-produtos {
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      padding: 24px;

      h3 {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0 0 20px 0;
        font-size: 1em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};

        svg {
          color: #8b5cf6;
        }
      }

      .produtos-table {
        .table-header {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          gap: 16px;
          padding: 12px 16px;
          background: ${({ theme }) => theme.buttonBg};
          border-radius: 8px;
          margin-bottom: 8px;

          span {
            font-size: 0.75em;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        }

        .table-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          gap: 16px;
          padding: 14px 16px;
          border-bottom: 1px solid ${({ theme }) => theme.border};
          align-items: center;

          &:last-child {
            border-bottom: none;
          }

          .produto-info {
            display: flex;
            flex-direction: column;
            gap: 2px;

            .produto-nome {
              font-size: 0.85em;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
            }

            .produto-codigo {
              font-size: 0.7em;
              color: ${({ theme }) => theme.text};
              opacity: 0.5;
            }
          }

          .valor {
            font-size: 0.85em;
            font-weight: 600;
            font-family: monospace;

            &.atual {
              color: ${tokens.danger};
            }

            &.novo {
              color: ${tokens.brand};
            }
          }

          .variacao {
            font-size: 0.85em;
            font-weight: 700;
            padding: 4px 8px;
            border-radius: 4px;
            text-align: center;

            &.positiva {
              background: rgba(239, 68, 68, 0.15);
              color: ${tokens.danger};
            }

            &.negativa {
              background: rgba(16, 185, 129, 0.15);
              color: ${tokens.brand};
            }
          }
        }
      }
    }
  }

  @media (max-width: 1024px) {
    .reforma-tab {
      .reforma-header {
        flex-direction: column;
        gap: 16px;
        text-align: center;

        .reforma-title {
          flex-direction: column;
        }
      }

      .reforma-comparison {
        flex-direction: column;

        .transicao-arrow {
          transform: rotate(90deg);
          padding: 16px 0;
        }
      }

      .reforma-timeline .timeline-items {
        flex-direction: column;
      }
    }
  }

  /* ========== REFORMA TAB ========== */
  .reforma-tab {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
  }

  .cenario-buttons {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;

    .cenario-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 14px 28px;
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-left: 4px solid transparent;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.25s ease;

      &:hover {
        border-left-color: #3b82f6;
        background: rgba(59, 130, 246, 0.05);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
      }

      &.active {
        border-left-color: #3b82f6;
        background: rgba(59, 130, 246, 0.08);
        box-shadow: 0 4px 16px rgba(59, 130, 246, 0.15);

        .cenario-year {
          color: #3b82f6;
        }

        .cenario-label {
          opacity: 0.8;
        }
      }

      .cenario-year {
        font-size: 1.2em;
        font-weight: 700;
        color: ${({ theme }) => theme.text};
        font-family: "Poppins", sans-serif;
        letter-spacing: -0.02em;
      }

      .cenario-label {
        font-size: 0.8em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        font-weight: 500;
      }
    }

    @media (max-width: 768px) {
      flex-wrap: wrap;

      .cenario-btn {
        flex: 1;
        min-width: 120px;
        padding: 12px 20px;
      }
    }
  }

  .reforma-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: all 0.2s ease;
    position: relative;

    &:hover {
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      transform: translateY(-2px);
    }

    &.main {
      border-left: 4px solid ${({ theme }) => theme.primary};
    }

    &.featured {
      padding: 24px 28px;

      &.yellow-border {
        border-left: 4px solid ${tokens.warn};
      }

      &.green-border {
        border-left: 4px solid ${tokens.brand};
      }
    }

    .card-icon {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      background: rgba(16, 185, 129, 0.1);
      color: ${({ theme }) => theme.primary};
      font-size: 1.1em;
      margin-bottom: 8px;

      &.yellow {
        background: rgba(245, 158, 11, 0.1);
        color: #f59e0b;
      }

      &.blue {
        background: rgba(59, 130, 246, 0.1);
        color: #3b82f6;
      }

      &.purple {
        background: rgba(139, 92, 246, 0.1);
        color: #8b5cf6;
      }

      &.green {
        background: rgba(16, 185, 129, 0.1);
        color: #10b981;
      }
    }

    .card-label {
      font-size: 0.8em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      font-weight: 500;
    }

    .card-value {
      font-size: 1.3em;
      font-weight: 700;
      color: ${({ theme }) => theme.text};
      font-family: "Poppins", sans-serif;
      letter-spacing: -0.02em;

      &.large {
        font-size: 1.6em;
      }

      &.highlight {
        color: #8b5cf6;
      }

      &.green {
        color: #10b981;
      }
    }

    .card-desc {
      font-size: 0.75em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }

    .card-breakdown {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid ${({ theme }) => theme.border};

      .breakdown-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;

        @media (max-width: 768px) {
          grid-template-columns: 1fr;
          gap: 12px;
        }
      }

      .breakdown-item {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .item-label {
          font-size: 0.7em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .item-value {
          font-size: 1em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          font-family: "Poppins", sans-serif;

          &.highlight {
            color: #8b5cf6;
            font-size: 1.1em;
          }
        }
      }
    }
  }

  .reforma-search {
    margin-top: 8px;
    margin-bottom: 12px;

    .search-input-wrapper {
      position: relative;
      width: 100%;

      .search-icon {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 1.1em;
      }

      .search-input {
        width: 100%;
        padding: 14px 16px 14px 48px;
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 12px;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        transition: all 0.2s ease;

        &::placeholder {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
        }

        &:focus {
          outline: none;
          border-color: ${({ theme }) => theme.primary};
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
      }
    }
  }

  .reforma-content-row {
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 16px;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
    }
  }

  .reforma-products-table {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    overflow: hidden;

    .table-header {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
      gap: 12px;
      padding: 14px 20px;
      background: ${({ theme }) => theme.cardBg};
      border-bottom: 1px solid ${({ theme }) => theme.border};

      .header-cell {
        font-size: 0.75em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-align: center;

        &.produto {
          text-align: left;
        }
      }
    }

    .table-body {
      max-height: 600px;
      overflow-y: overlay;

      &::-webkit-scrollbar {
        width: 4px;
      }
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.border};
        border-radius: 2px;
      }
    }

    .product-row {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
      gap: 12px;
      padding: 16px 20px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      border-left: 3px solid var(--product-color, #64748b);
      transition: background ${tokens.durBase} ${tokens.easeOut},
                  opacity ${tokens.durBase} ${tokens.easeOut};
      cursor: pointer;
      opacity: 0.7;

      &:hover {
        background: ${({ theme }) => surface(theme, 1)};
        opacity: 1;
      }

      &:last-child {
        border-bottom: none;
      }

      .product-info {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 12px;

        .product-icon {
          flex-shrink: 0;
          font-size: 1.1em;
          color: var(--product-color, ${({ theme }) => theme.text});
          opacity: 0.7;
        }

        .product-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;

          .product-code {
            ${mono};
            font-size: 0.7em;
            color: var(--product-color, #64748b);
            font-weight: 600;
            opacity: 0.8;
          }

          .product-name {
            font-size: 0.82em;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
            opacity: 0.9;
            line-height: 1.3;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }

      .tax-value {
        ${mono};
        font-size: 0.82em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.65;
        text-align: center;
      }
    }
  }

  .impacto-sidebar {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .impacto-main-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-left: 4px solid #ef4444;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    .impacto-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
    }

    .impacto-icon {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(239, 68, 68, 0.1);
      border-radius: 8px;
      color: #ef4444;
      font-size: 1.1em;
    }

    .impacto-badge {
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 0.65em;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;

      &.alto {
        background: rgba(239, 68, 68, 0.15);
        color: #ef4444;
      }

      &.medio {
        background: rgba(245, 158, 11, 0.15);
        color: #f59e0b;
      }

      &.baixo {
        background: rgba(16, 185, 129, 0.15);
        color: #10b981;
      }
    }

    .impacto-title {
      font-size: 0.8em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
    }

    .impacto-value {
      font-size: 1.3em;
      font-weight: 700;
      color: ${({ theme }) => theme.text};
      font-family: "Poppins", sans-serif;
      letter-spacing: -0.02em;
    }

    .impacto-desc {
      font-size: 0.75em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }

    .impacto-variation {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 6px;
      padding-top: 10px;
      border-top: 1px solid ${({ theme }) => theme.border};

      svg {
        color: #ef4444;
        font-size: 1em;
      }

      span {
        font-size: 0.9em;
        font-weight: 700;
        color: #ef4444;
      }

      .variation-label {
        font-size: 0.75em;
        font-weight: 400;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
      }
    }
  }

  .impacto-compare-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 14px;

    .compare-title {
      font-size: 0.75em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      display: block;
      margin-bottom: 10px;
    }

    .compare-items {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .compare-item {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &.highlight {
        padding-top: 10px;
        margin-top: 4px;
        border-top: 1px solid ${({ theme }) => theme.border};
      }

      .compare-label {
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
      }

      .compare-value {
        font-size: 0.85em;
        font-weight: 600;
        font-family: "Poppins", sans-serif;

        &.atual {
          color: ${({ theme }) => theme.text};
        }

        &.novo {
          color: #3b82f6;
        }

        &.diff {
          color: #ef4444;
        }
      }
    }
  }

  .impacto-aliquotas-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 14px;

    .aliquotas-title {
      font-size: 0.75em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      display: block;
      margin-bottom: 10px;
    }

    .aliquota-row {
      display: flex;
      gap: 10px;
      margin-bottom: 10px;
    }

    .aliquota-item {
      flex: 1;
      background: ${({ theme }) => theme.buttonBg};
      border-radius: 8px;
      padding: 10px;
      text-align: center;

      .aliquota-sigla {
        display: block;
        font-size: 0.7em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        margin-bottom: 4px;
      }

      .aliquota-valor {
        font-size: 1.1em;
        font-weight: 700;
        color: #3b82f6;
        font-family: "Poppins", sans-serif;
      }
    }

    .aliquota-total {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 12px;
      border-top: 1px solid ${({ theme }) => theme.border};

      span {
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
      }

      .total-valor {
        font-size: 1em;
        font-weight: 700;
        color: ${({ theme }) => theme.primary};
        opacity: 1;
        font-family: "Poppins", sans-serif;
      }
    }
  }

  .reforma-charts-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 16px;

    .reforma-chart-card {
      flex: 1;
    }

    @media (max-width: 1200px) {
      flex-direction: column;
    }
  }

  .reforma-chart-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;

    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 8px;

      .chart-title {
        display: flex;
        align-items: center;
        gap: 10px;

        .chart-icon {
          font-size: 1.2em;
          color: #3b82f6;
        }

        span {
          font-size: 0.95em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }
      }

      .chart-subtitle {
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
      }
    }

    .chart-container {
      flex: 1;
      min-height: auto;
      padding: 0;
    }

    .chart-legend {
      display: flex;
      justify-content: center;
      gap: 20px;
      margin-top: 0;
      padding-top: 8px;
      border-top: none;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 8px;

        .legend-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }

        span {
          font-size: 0.8em;
          color: ${({ theme }) => theme.text};
          opacity: 0.7;
        }
      }
    }

    .chart-footer {
      display: flex;
      justify-content: space-around;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid ${({ theme }) => theme.border};

      .footer-stat {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;

        .stat-label {
          font-size: 0.75em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }

        .stat-value {
          font-size: 1.1em;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
          font-family: "Poppins", sans-serif;

          &.increase {
            color: #ef4444;
          }

          &.decrease {
            color: #10b981;
          }
        }
      }
    }
  }

  .impacto-timeline-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 16px;

    .timeline-title {
      font-size: 0.75em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      display: block;
      margin-bottom: 16px;
    }

    .timeline-steps {
      display: flex;
      justify-content: space-between;
      position: relative;
      padding: 0 8px;

      &::before {
        content: "";
        position: absolute;
        top: 18px;
        left: 32px;
        right: 32px;
        height: 3px;
        background: linear-gradient(
          90deg,
          ${({ theme }) => theme.primary} 0%,
          ${({ theme }) => theme.border} 33%,
          ${({ theme }) => theme.border} 100%
        );
        border-radius: 2px;
      }
    }

    .timeline-step {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      position: relative;
      z-index: 1;

      .step-year {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${({ theme }) => theme.buttonBg};
        border: 2px solid ${({ theme }) => theme.border};
        border-radius: 50%;
        font-size: 0.75em;
        font-weight: 700;
        color: ${({ theme }) => theme.text};
        transition: all 0.2s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .step-desc {
        font-size: 0.7em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        font-weight: 500;
      }

      &.active {
        .step-year {
          background: ${({ theme }) => theme.primary};
          border-color: ${({ theme }) => theme.primary};
          color: #fff;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          transform: scale(1.1);
        }

        .step-desc {
          color: ${({ theme }) => theme.primary};
          opacity: 1;
          font-weight: 600;
        }
      }

      &:hover:not(.active) {
        .step-year {
          border-color: ${({ theme }) => theme.primary};
          transform: scale(1.05);
        }
      }
    }
  }

  /* Nova Tabela de Projeção de Débitos - Moderna */
  .projecao-debitos-section {
    margin-top: 24px;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 16px;
    overflow: hidden;

    .section-header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px;
      border-bottom: 1px solid ${({ theme }) => theme.border};
      background: ${({ theme }) => theme.cardBg};

      .section-title {
        display: flex;
        align-items: center;
        gap: 12px;

        .section-icon {
          font-size: 1.4em;
          color: ${({ theme }) => theme.primary};
        }

        h3 {
          font-size: 1.1em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          margin: 0;
        }
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: 16px;

        .table-search-input {
          position: relative;
          display: flex;
          align-items: center;

          .search-icon {
            position: absolute;
            left: 12px;
            font-size: 1.1em;
            color: ${({ theme }) => theme.primary};
            opacity: 0.8;
          }

          input {
            padding: 8px 16px 8px 40px;
            border: 2px solid ${({ theme }) => theme.border};
            border-radius: 8px;
            background: ${({ theme }) => theme.background};
            color: ${({ theme }) => theme.text};
            font-size: 0.9em;
            font-weight: 500;
            width: 400px;
            transition: all 0.2s ease;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

            &::placeholder {
              color: ${({ theme }) => theme.text};
              opacity: 0.5;
            }

            &:hover {
              border-color: ${({ theme }) => theme.primary};
              box-shadow: 0 4px 12px rgba(16, 185, 129, 0.1);
            }

            &:focus {
              outline: none;
              border-color: ${({ theme }) => theme.primary};
              box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.15);
            }
          }
        }

        .products-count {
          font-size: 0.85em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          background: ${({ theme }) => theme.buttonBg};
          padding: 6px 12px;
          border-radius: 20px;
        }
      }
    }

    .projecao-table-container {
      overflow-x: auto;

      &::-webkit-scrollbar {
        height: 6px;
      }
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.border};
        border-radius: 3px;
      }
    }

    .projecao-table {
      width: 100%;
      border-collapse: collapse;
      min-width: 1100px;

      thead {
        tr:first-child {
          th {
            padding: 16px 12px 8px;
            font-size: 0.7em;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            text-align: left;
            border-bottom: none;

            .header-group {
              display: inline-block;
              padding: 6px 14px;
              border-radius: 6px;
              background: ${({ theme }) => theme.buttonBg};
              font-size: 1.1em;

              &.cbs {
                background: rgba(59, 130, 246, 0.1);
                color: ${tokens.info};
              }

              &.variacao {
                background: rgba(139, 92, 246, 0.1);
                color: ${tokens.warn};
              }
            }
          }
        }

        tr.sub-header {
          th {
            padding: 8px 12px 12px;
            font-size: 0.7em;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
            opacity: 0.4;
            text-align: left;
            border-bottom: 1px solid ${({ theme }) => theme.border};

            &.sub-col {
              text-align: center;
            }
          }
        }
      }

      tbody {
        tr.data-row {
          transition: all 0.2s ease;
          border-left: 3px solid transparent;

          &:hover {
            background: ${({ theme }) => surface(theme, 1)};
          }

          &.reducao {
            border-left-color: #10b981;
          }

          &.aumento {
            border-left-color: #ef4444;
          }

          &.neutro {
            border-left-color: #f59e0b;
          }

          td {
            padding: 14px 12px;
            font-size: 0.85em;
            color: ${({ theme }) => theme.text};
            border-bottom: 1px solid ${({ theme }) => theme.border};
            vertical-align: middle;

            &.col-codigo {
              .codigo-badge {
                font-family: "SF Mono", "Consolas", monospace;
                font-size: 0.85em;
                font-weight: 600;
                color: ${({ theme }) => theme.primary};
                background: rgba(16, 185, 129, 0.1);
                padding: 4px 10px;
                border-radius: 6px;
              }
            }

            &.col-descricao {
              .produto-nome {
                font-weight: 500;
                color: ${({ theme }) => theme.text};
                opacity: 0.9;
              }
            }

            &.col-valor {
              .valor-principal {
                font-weight: 600;
                font-family: "Poppins", sans-serif;
                color: ${({ theme }) => theme.text};
              }
            }

            &.col-percent {
              .percent-bar-container {
                display: flex;
                align-items: center;
                gap: 8px;

                .percent-bar {
                  height: 6px;
                  background: linear-gradient(
                    90deg,
                    ${({ theme }) => theme.primary},
                    #3b82f6
                  );
                  border-radius: 3px;
                  min-width: 4px;
                }

                .percent-value {
                  font-size: 0.9em;
                  font-weight: 500;
                  color: ${({ theme }) => theme.text};
                  opacity: 0.7;
                  white-space: nowrap;
                }
              }
            }

            &.col-debito,
            &.col-cbs {
              text-align: center;
              font-family: "Poppins", sans-serif;
              opacity: 0.8;

              &.total {
                font-weight: 600;
                opacity: 1;
                color: ${tokens.info};
              }
            }

            &.col-aliq {
              text-align: center;
              font-weight: 600;
              color: ${tokens.warn};
            }

            &.col-diferenca {
              text-align: center;
              font-weight: 700;
              font-family: "Poppins", sans-serif;

              &.reducao {
                color: ${tokens.brand};
              }

              &.aumento {
                color: ${tokens.danger};
              }

              &.neutro {
                color: ${tokens.warn};
              }
            }

            &.col-impacto {
              text-align: center;

              .impacto-badge {
                display: inline-block;
                padding: 6px 12px;
                border-radius: 20px;
                font-size: 0.75em;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.3px;

                &.reducao {
                  background: rgba(16, 185, 129, 0.15);
                  color: ${tokens.brand};
                }

                &.aumento {
                  background: rgba(239, 68, 68, 0.15);
                  color: ${tokens.danger};
                }

                &.neutro {
                  background: rgba(245, 158, 11, 0.15);
                  color: ${tokens.warn};
                }

                &.alto {
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
              }
            }
          }
        }
      }
    }

    .table-legend {
      display: flex;
      justify-content: center;
      gap: 20px;
      padding: 16px 24px;
      background: ${({ theme }) => theme.cardBg};
      border-top: 1px solid ${({ theme }) => theme.border};
      flex-wrap: wrap;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;

        .legend-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        &.reducao .legend-dot {
          background: #10b981;
        }

        &.reducao-leve .legend-dot {
          background: #34d399;
        }

        &.neutro .legend-dot {
          background: #f59e0b;
        }

        &.aumento-leve .legend-dot {
          background: #f87171;
        }

        &.aumento .legend-dot {
          background: #ef4444;
        }
      }

      /* Legenda para Créditos */
      &.creditos {
        .legend-item {
          &.ganho-alto .legend-dot {
            background: #10b981;
          }

          &.ganho .legend-dot {
            background: #34d399;
          }

          &.perda .legend-dot {
            background: #f87171;
          }

          &.perda-alta .legend-dot {
            background: #ef4444;
          }
        }
      }
    }

    /* Estilos específicos para tabela de Créditos */
    &.creditos {
      .section-title .section-icon {
        color: #3b82f6;
      }

      .projecao-table {
        tbody tr.data-row {
          &.ganho {
            border-left-color: #10b981;
          }

          &.perda {
            border-left-color: #ef4444;
          }

          td {
            &.col-codigo .codigo-badge.credito {
              color: ${tokens.info};
              background: rgba(59, 130, 246, 0.1);
            }

            &.col-qtd {
              text-align: center;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              opacity: 0.8;
            }

            &.col-credito-atual {
              text-align: center;
              font-family: "Poppins", sans-serif;
              opacity: 0.8;

              &.total {
                font-weight: 600;
                opacity: 1;
                color: ${tokens.brand};
              }
            }

            &.col-ganho-perda {
              text-align: center;
              font-weight: 700;
              font-family: "Poppins", sans-serif;

              &.ganho {
                color: ${tokens.brand};
              }

              &.perda {
                color: ${tokens.danger};
              }

              &.neutro {
                color: ${tokens.warn};
              }
            }

            .percent-bar.credito {
              background: linear-gradient(90deg, ${tokens.info}, #60a5fa);
            }

            .impacto-badge {
              &.ganho {
                background: rgba(16, 185, 129, 0.15);
                color: ${tokens.brand};
              }

              &.perda {
                background: rgba(239, 68, 68, 0.15);
                color: ${tokens.danger};
              }
            }
          }
        }
      }

      .header-group {
        &.creditos-atuais {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        &.variacao-creditos {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }
      }
    }
  }
`;

export const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;
  align-items: start;

  .products-table {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow: hidden;

    .table-header {
      display: grid;
      grid-template-columns: minmax(0, 1.8fr) minmax(0, 0.7fr) minmax(0, 0.7fr) minmax(0, 0.8fr) minmax(0, 0.8fr);
      gap: 12px;
      padding: 10px 16px;
      background: ${({ theme }) => surface(theme, 1)};
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      .header-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        font-size: 0.68em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        white-space: nowrap;
        ${mono};

        svg { font-size: 0.95em; opacity: 0.4; }

        &.produto { justify-content: flex-start; }
      }
    }

    .table-body {
      display: flex;
      flex-direction: column;
      max-height: 600px;
      overflow-y: auto;

      &::-webkit-scrollbar { width: 4px; }
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => hairline(theme, true)};
        border-radius: 2px;
      }
    }
  }

  .categories-card {
    height: 650px;
    position: sticky;
    top: 20px;
  }

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;

    .categories-card {
      position: static;
      height: 500px;
    }
  }
`;
